import type { CollectionDefaults, CollectionRelation, CollectionRelationReference, ResolvedCollection, StoreCore, StoreSchema } from '@rstore/shared';
import type { AnyItem, CompositePrimaryKey, ConnectToMany, CreateToMany, CreateToOne, DeleteToMany, DisconnectToMany, PrimitivePrimaryKey, RelationOperationToMany, RelationOperationToOne, UpdateToMany, UpdateToOne } from '#layers/data-model/public';
import { getCollectionPrimaryKeyFieldNames, isConnectToMany, isConnectToOne, isCreateToMany, isCreateToOne, isDeleteToMany, isDisconnectToMany, isDisconnectToOne, isFilterOperator, isUpdateToMany, isUpdateToOne } from '#layers/data-model/public';
import { resolveSchemaCollection } from './resolve-schema-collection';

type CacheStore = StoreCore<StoreSchema, CollectionDefaults>;

/**
 * Store data the collectors read: the rstore collections relation targets resolve against, and
 * each collection's primary key field names, used to tell a complete key from a partial one.
 */
export interface CacheOperationsContext {
	/** Every rstore collection a relation target can resolve against. */
	collections: ResolvedCollection[];
	/**
	 * The collection's primary key field names, or `undefined` for a collection outside the
	 * workspace schema (system collections), whose keys are taken as `getKey` returns them.
	 */
	getPrimaryKeyFields: (collection: ResolvedCollection) => string[] | undefined;
}

function resolveRelationRef(
	ref: CollectionRelationReference | CollectionRelationReference[] | undefined,
): CollectionRelationReference[] {
	if (!ref) return [];
	return Array.isArray(ref) ? ref : [ref];
}

/**
 * Field-name pairs from a relation's `on` map. The rstore type widens the key/value to
 * `string | number | symbol`, but relation `on` maps are always field-name → field-name, so
 * this narrows them to `[string, string]` once at the boundary instead of casting per use.
 */
function relationFieldPairs(on: CollectionRelationReference['on']): [string, string][] {
	return Object.entries(on) as [string, string][];
}

/**
 * Checks if a value is any relation operation.
 */
export function isRelationOperation(value: unknown) {
	if (!value || typeof value !== 'object') return false;
	return '_create' in value || '_connect' in value || '_update' in value
		|| '_delete' in value || '_disconnect' in value;
}

/**
 * Gets the relation definition for a field.
 */
export function getRelation(
	collection: ResolvedCollection,
	fieldName: string,
): CollectionRelation | null {
	const relations = collection.relations;
	return relations?.[fieldName] ?? null;
}

/**
 * Finds the target collection for a relation.
 */
export function findTargetCollection(
	collections: ResolvedCollection[],
	relation: CollectionRelation,
): ResolvedCollection | null {
	for (const collectionKey in relation.to) {
		const collection = collections.find((c) => c.name === collectionKey);
		if (collection) return collection;
	}

	return null;
}

/**
 * Extracts PK fields from item data as CompositePrimaryKey for recursive calls.
 */
export function extractPKFromItem(
	item: AnyItem,
	collection: ResolvedCollection,
): CompositePrimaryKey {
	const pk: CompositePrimaryKey = {};

	const pkFields = collection.meta?.primaryKeyFields ?? ['id'];

	for (const field of pkFields) {
		if (item[field] !== undefined) {
			pk[field] = item[field];
		}
	}

	return pk;
}

/**
 * Converts a filter to a cache key.
 */
export function filterToKey(
	filter: Record<string, unknown> | CompositePrimaryKey,
	collection: ResolvedCollection,
): PrimitivePrimaryKey | null {
	const filterKeys = Object.keys(filter);

	// Check if any top-level key is a filter operator (_and, _or, _not)
	const hasTopLevelOperator = filterKeys.some((k) => isFilterOperator(k));
	if (hasTopLevelOperator) return null;

	// Check if any value is an object containing filter operators
	const hasNestedOperator = filterKeys.some((k) => {
		const value = filter[k];

		if (value && typeof value === 'object' && !Array.isArray(value)) {
			return Object.keys(value).some((nestedKey) => isFilterOperator(nestedKey));
		}

		return false;
	});
	if (hasNestedOperator) return null;

	return collection.getKey(filter as CompositePrimaryKey) ?? null;
}

/**
 * Extracts FK values from parent for to-many relations.
 */
export function extractParentFK(
	relation: CollectionRelation,
	targetCollection: ResolvedCollection,
	parentPK?: CompositePrimaryKey,
): CompositePrimaryKey {
	if (!parentPK) return {};

	const fks: CompositePrimaryKey = {};
	const refs = resolveRelationRef(relation.to[targetCollection.name]);

	for (const ref of refs) {
		if (!ref.on) continue;

		for (const [fkField, pkField] of relationFieldPairs(ref.on)) {
			const value = parentPK[pkField];

			if (value !== undefined) {
				fks[fkField] = value;
			}
		}
	}

	return fks;
}

/**
 * Extracts FK values from to-one _connect and _create operations.
 */
export function extractToOneFKs(
	payload: AnyItem,
	collection: ResolvedCollection,
): AnyItem {
	const fks: AnyItem = {};

	for (const [fieldName, value] of Object.entries(payload)) {
		const relation = getRelation(collection, fieldName);
		if (!relation || relation.many) continue;

		const ops = Array.isArray(value) ? value : [value];

		for (const op of ops) {
			if (!isRelationOperation(op)) continue;

			let sourceObject: AnyItem | null = null;
			let isNullify = false;

			if (isConnectToOne(op)) {
				sourceObject = op._connect.key;
			}
			else if (isCreateToOne(op)) {
				sourceObject = op._create.data;
			}
			else if (isDisconnectToOne(op)) {
				isNullify = true;
			}

			if (!sourceObject && !isNullify) continue;

			const refs = resolveRelationRef(Object.values(relation.to)[0]);

			for (const ref of refs) {
				if (!ref.on) continue;

				for (const [targetField, localFkField] of relationFieldPairs(ref.on)) {
					if (isNullify) {
						fks[localFkField] = null;
					}
					else if (sourceObject) {
						const val = sourceObject[targetField];

						if (val !== undefined) {
							fks[localFkField] = val;
						}
					}
				}
			}
		}
	}

	return fks;
}

/**
 * Returns FK fields set to null for disconnect operations.
 */
export function extractNullFK(
	relation: CollectionRelation,
	targetCollection: ResolvedCollection,
): AnyItem {
	const fks: AnyItem = {};
	const refs = resolveRelationRef(relation.to[targetCollection.name]);

	for (const ref of refs) {
		if (!ref.on) continue;

		for (const fkField of Object.keys(ref.on)) {
			fks[fkField] = null;
		}
	}

	return fks;
}

/**
 * Whether the item carries a value for every primary key field of the collection.
 *
 * A nested `_create` has a server-generated key the client does not know yet, and a composite
 * `getKey` still stringifies the fields it has. Writing under that partial key leaves a ghost row:
 * the server never returns that key, so no refetch replaces it, and the row keeps resolving
 * through FK indexes (as an extra M2M junction, for instance) until a full reload.
 *
 * The check is on the value (`!= null`), not on key presence: `buildItemData` copies `undefined`
 * values through, so a presence test (`in`) would accept `{ TrackId: undefined }`. That is why the
 * presence-based `isPrimaryKeyComplete` in `layers/data-model/utils/primary-key.ts` is not reused.
 */
function hasCompletePrimaryKey(ctx: CacheOperationsContext, item: AnyItem, collection: ResolvedCollection): boolean {
	const primaryKeyFields = ctx.getPrimaryKeyFields(collection);
	if (!primaryKeyFields) return false;

	return primaryKeyFields.every((field) => item[field] != null);
}

/**
 * Builds item data for cache write by:
 * 1. Copying primitive fields
 * 2. Adding FK from parent (for to-many relations)
 * 3. Extracting FKs from to-one _connect/_create operations
 * 4. Nullifying FKs from to-one _disconnect operations
 */
export function buildItemData(
	payload: AnyItem,
	relation: CollectionRelation,
	targetCollection: ResolvedCollection,
	parentPK?: CompositePrimaryKey,
): AnyItem {
	const result: AnyItem = {};

	for (const [field, value] of Object.entries(payload)) {
		if (value === null || value === undefined) {
			result[field] = value;
			continue;
		}

		if (Array.isArray(value)) continue;
		if (typeof value === 'object' && isRelationOperation(value)) continue;

		if (typeof value !== 'object') {
			result[field] = value;
		}
		else {
			const fieldRelation = getRelation(targetCollection, field);

			if (!fieldRelation) {
				result[field] = value;
			}
		}
	}

	if (parentPK) {
		Object.assign(result, extractParentFK(relation, targetCollection, parentPK));
	}

	Object.assign(result, extractToOneFKs(payload, targetCollection));

	return result;
}

interface CacheWriteOp {
	type: 'WRITE';
	collection: ResolvedCollection;
	key: PrimitivePrimaryKey;
	data: AnyItem;
}

interface CacheDeleteOp {
	type: 'DELETE';
	collection: ResolvedCollection;
	key: PrimitivePrimaryKey;
}

type CacheOp = CacheWriteOp | CacheDeleteOp;

/**
 * Collects all cache operations from a payload (recursive).
 */
export function collectCacheOperations(
	ctx: CacheOperationsContext,
	payload: AnyItem,
	collection: ResolvedCollection,
	parentPK?: CompositePrimaryKey,
	visited: Set<string> = new Set(),
): CacheOp[] {
	const ops: CacheOp[] = [];

	// Circular reference detection
	const visitKey = `${collection.name}:${JSON.stringify(parentPK ?? {})}`;

	if (visited.has(visitKey)) {
		console.warn('[applyRelationOperationsToCache] Circular reference detected:', {
			collection: collection.name,
			parentPK,
		});

		return ops;
	}

	visited.add(visitKey);

	for (const [fieldName, value] of Object.entries(payload)) {
		if (value === null || value === undefined) continue;

		const relation = getRelation(collection, fieldName);
		if (!relation) continue;

		const targetCollection = findTargetCollection(ctx.collections, relation);
		if (!targetCollection) continue;

		if (relation.many) {
			ops.push(...processToManyOperations(ctx, value as RelationOperationToMany[], relation, targetCollection, parentPK, visited));
		}
		else if (Array.isArray(value)) {
			// To-one in update context: array of SingleToOneOperation
			for (const op of value) {
				ops.push(...processToOneOperation(ctx, op as RelationOperationToOne, relation, targetCollection, parentPK, visited));
			}
		}
		else if (typeof value === 'object') {
			ops.push(...processToOneOperation(ctx, value, relation, targetCollection, parentPK, visited));
		}
	}

	return ops;
}

function processToManyOperations(
	ctx: CacheOperationsContext,
	operations: RelationOperationToMany[],
	relation: CollectionRelation,
	targetCollection: ResolvedCollection,
	parentPK?: CompositePrimaryKey,
	visited?: Set<string>,
): CacheOp[] {
	const ops: CacheOp[] = [];

	for (const operation of operations) {
		if (isCreateToMany(operation)) {
			ops.push(...processCreateToMany(ctx, operation, relation, targetCollection, parentPK, visited));
		}
		else if (isConnectToMany(operation)) {
			ops.push(...processConnectToMany(ctx, operation, relation, targetCollection, parentPK));
		}
		else if (isUpdateToMany(operation)) {
			ops.push(...processUpdateToMany(ctx, operation, relation, targetCollection, visited));
		}
		else if (isDeleteToMany(operation)) {
			ops.push(...processDeleteToMany(operation, targetCollection));
		}
		else if (isDisconnectToMany(operation)) {
			ops.push(...processDisconnectToMany(operation, relation, targetCollection));
		}
		else {
			console.error('[applyRelationOperationsToCache] Unknown to-many operation:', operation);
		}
	}

	return ops;
}

function processCreateToMany(
	ctx: CacheOperationsContext,
	operation: CreateToMany,
	relation: CollectionRelation,
	targetCollection: ResolvedCollection,
	parentPK?: CompositePrimaryKey,
	visited?: Set<string>,
): CacheOp[] {
	const ops: CacheOp[] = [];

	for (const itemPayload of operation._create.data) {
		const itemData = buildItemData(itemPayload, relation, targetCollection, parentPK);

		// Server-generated key: the refetch after the mutation fills the cache instead.
		if (!hasCompletePrimaryKey(ctx, itemData, targetCollection)) continue;

		const key = targetCollection.getKey(itemData);

		if (!key) {
			console.error('[applyRelationOperationsToCache] Failed to generate key for create:', {
				collection: targetCollection.name,
				itemData,
			});

			continue;
		}

		ops.push({ type: 'WRITE', collection: targetCollection, key, data: itemData });

		const itemPK = extractPKFromItem(itemData, targetCollection);
		ops.push(...collectCacheOperations(ctx, itemPayload, targetCollection, itemPK, visited));
	}

	return ops;
}

function processConnectToMany(
	ctx: CacheOperationsContext,
	operation: ConnectToMany,
	relation: CollectionRelation,
	targetCollection: ResolvedCollection,
	parentPK?: CompositePrimaryKey,
): CacheOp[] {
	const ops: CacheOp[] = [];
	const fkData = extractParentFK(relation, targetCollection, parentPK);

	for (const connectKey of operation._connect.keys) {
		const key = targetCollection.getKey(connectKey);

		if (!key) {
			console.error('[applyRelationOperationsToCache] Failed to generate key for connect:', {
				collection: targetCollection.name,
				connectKey,
			});

			continue;
		}

		// The connected row may not be cached yet, in which case rstore creates it from this body
		// alone. Include the row's own key fields, or the cached body holds only the parent FK columns
		// and any key rebuilt from it is partial. Parent FKs win on overlap.
		const data: AnyItem = { ...connectKey, ...fkData };

		// Same guard as a nested create: never key a row whose body lacks part of its own key.
		if (!hasCompletePrimaryKey(ctx, data, targetCollection)) continue;

		ops.push({ type: 'WRITE', collection: targetCollection, key, data });
	}

	return ops;
}

function processUpdateToMany(
	ctx: CacheOperationsContext,
	operation: UpdateToMany,
	relation: CollectionRelation,
	targetCollection: ResolvedCollection,
	visited?: Set<string>,
): CacheOp[] {
	const ops: CacheOp[] = [];

	const { filter, data } = operation._update;
	if (!filter) return ops;

	const key = filterToKey(filter as Record<string, unknown>, targetCollection);

	if (!key) {
		console.error('[applyRelationOperationsToCache] Cannot resolve key from update filter:', {
			collection: targetCollection.name,
			filter,
		});

		return ops;
	}

	const updateData = buildItemData(data, relation, targetCollection);

	if (Object.keys(updateData).length > 0) {
		ops.push({ type: 'WRITE', collection: targetCollection, key, data: updateData });
	}

	ops.push(...collectCacheOperations(ctx, data, targetCollection, filter as CompositePrimaryKey, visited));

	return ops;
}

function processDeleteToMany(
	operation: DeleteToMany,
	targetCollection: ResolvedCollection,
): CacheOp[] {
	const { filter } = operation._delete;
	const key = filterToKey(filter as Record<string, unknown>, targetCollection);

	if (!key) {
		console.error('[applyRelationOperationsToCache] Cannot resolve key from delete filter:', {
			collection: targetCollection.name,
			filter,
		});

		return [];
	}

	return [{ type: 'DELETE', collection: targetCollection, key }];
}

function processDisconnectToMany(
	operation: DisconnectToMany,
	relation: CollectionRelation,
	targetCollection: ResolvedCollection,
): CacheOp[] {
	const { filter } = operation._disconnect;
	const key = filterToKey(filter as Record<string, unknown>, targetCollection);

	if (!key) {
		console.error('[applyRelationOperationsToCache] Cannot resolve key from disconnect filter:', {
			collection: targetCollection.name,
			filter,
		});

		return [];
	}

	const nullFKs = extractNullFK(relation, targetCollection);

	return [{ type: 'WRITE', collection: targetCollection, key, data: nullFKs }];
}

function processToOneOperation(
	ctx: CacheOperationsContext,
	op: RelationOperationToOne,
	relation: CollectionRelation,
	targetCollection: ResolvedCollection,
	parentPK?: CompositePrimaryKey,
	visited?: Set<string>,
): CacheOp[] {
	if (isConnectToOne(op)) {
		return [];
	}

	if (isCreateToOne(op)) {
		return processCreateToOne(ctx, op, relation, targetCollection, parentPK, visited);
	}

	if (isUpdateToOne(op)) {
		return processUpdateToOne(ctx, op, relation, targetCollection, visited);
	}

	if (isDisconnectToOne(op)) {
		return [];
	}

	console.error('[applyRelationOperationsToCache] Unknown to-one operation:', op);
	return [];
}

function processCreateToOne(
	ctx: CacheOperationsContext,
	operation: CreateToOne,
	relation: CollectionRelation,
	targetCollection: ResolvedCollection,
	parentPK?: CompositePrimaryKey,
	visited?: Set<string>,
): CacheOp[] {
	const ops: CacheOp[] = [];
	const itemPayload = operation._create.data;

	const itemData = buildItemData(itemPayload, relation, targetCollection, parentPK);

	// Server-generated key: the refetch after the mutation fills the cache instead.
	if (!hasCompletePrimaryKey(ctx, itemData, targetCollection)) return ops;

	const key = targetCollection.getKey(itemData);

	if (!key) {
		console.error('[applyRelationOperationsToCache] Failed to generate key for to-one create:', {
			collection: targetCollection.name,
			itemData,
		});

		return ops;
	}

	ops.push({ type: 'WRITE', collection: targetCollection, key, data: itemData });

	const itemPK = extractPKFromItem(itemData, targetCollection);
	ops.push(...collectCacheOperations(ctx, itemPayload, targetCollection, itemPK, visited));

	return ops;
}

function processUpdateToOne(
	ctx: CacheOperationsContext,
	operation: UpdateToOne,
	relation: CollectionRelation,
	targetCollection: ResolvedCollection,
	visited?: Set<string>,
): CacheOp[] {
	const ops: CacheOp[] = [];

	const { filter, data } = operation._update;

	if (!filter) {
		console.error('[applyRelationOperationsToCache] UpdateToOne missing filter:', operation);
		return ops;
	}

	const key = filterToKey(filter as Record<string, unknown>, targetCollection);

	if (!key) {
		console.error('[applyRelationOperationsToCache] Cannot resolve key from to-one update filter:', {
			collection: targetCollection.name,
			filter,
		});

		return ops;
	}

	const updateData = buildItemData(data, relation, targetCollection);

	if (Object.keys(updateData).length > 0) {
		ops.push({ type: 'WRITE', collection: targetCollection, key, data: updateData });
	}

	ops.push(...collectCacheOperations(ctx, data, targetCollection, filter as CompositePrimaryKey, visited));

	return ops;
}

/**
 * Main entry point. Processes a payload and applies all cache operations.
 *
 * Primary key fields come from the workspace schema (`MonospaceCollection`); rstore's own
 * `meta.primaryKeyFields` is never populated for workspace collections.
 */
export function applyRelationOperationsToCache(
	store: CacheStore,
	payload: AnyItem,
	collection: ResolvedCollection,
	parentPK?: CompositePrimaryKey,
) {
	const ctx: CacheOperationsContext = {
		collections: store.$collections,
		getPrimaryKeyFields: (target) => {
			const schemaCollection = resolveSchemaCollection({ collectionName: target.name, store });

			return schemaCollection ? getCollectionPrimaryKeyFieldNames(schemaCollection) : undefined;
		},
	};

	const ops = collectCacheOperations(ctx, payload, collection, parentPK);

	for (const op of ops) {
		if (op.type === 'WRITE') {
			store.$cache.writeItem({
				collection: op.collection,
				key: op.key,
				item: op.data,
			});
		}
		else {
			store.$cache.deleteItem({
				collection: op.collection,
				key: op.key,
			});
		}
	}
}
